import React, { useState } from 'react';

import styled from 'styled-components';

import BuilderCategoryNavRenderer from '~/grid/components/categoryNav/BuilderCategoryNavRenderer';
import ToastContext from '~/shared/components/Toast/ToastContext';
import ToastMessages from '~/shared/components/Toast/ToastMessages';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';

import { useAutoFilters } from '../hooks/useAutofilters';
import useGridInputs from '../hooks/useGridInputs';
import useInGridAssets from '../hooks/useInGridAssets';
import useUnifiedProductGridData from '../hooks/useUnifiedProductGridData';
import { getGridLayoutTrackingTags } from '../utils/tracking';

import MobileGridDiscoveryGuide from './DiscoveryGuide/MobileGridDiscoveryGuide';
import GridLayoutTrackingProvider from './GridLayoutTrackingProvider';
import GridSpotlights from './GridSpotlights';
import MobileGridHeader from './MobileGridHeader';
import MobileGridVirtualScroll from './MobileGridVirtualScroll';
import VirtualScrollGrid from './VirtualScrollGrid';

const GridContainer = styled.div`
  position: relative;
`;

const MobileGridLayout = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const {
    products,
    totalNumResults,
    fetchNextPage,
    hasNextPage,
    isPrimaryProductsFetching,
    isPrimaryProductsLoading,
    facets,
    cioFilterName,
    cioFilterValue,
  } = useUnifiedProductGridData();

  const { enabled: showBuilderSideNav } = useHasFeature(
    featureNames.BUILDER_CATEGORY_SIDE_NAV
  );
  const { enabled: isNewPricingUiEnabled } = useHasFeature(
    featureNames.PRICING_UI_AB
  );

  const { mergedAssetsAndProducts } = useInGridAssets({
    products,
    // During the Pricing UI AB test, ignore multi-row or multi-column assets in the column eligibility check
    ignoreMultiRowAndMultiColumnAssets: isNewPricingUiEnabled,
  });
  const { builderState, gridGender, gridType, isQuickViewEnabled } =
    useGridInputs();

  useAutoFilters({
    builderState,
    gridGender,
    facets,
    isPrimaryProductsLoading,
  });

  const { trackingTags } = getGridLayoutTrackingTags({
    totalNumResults,
    cioFilterName,
    cioFilterValue,
    gridType,
  });

  const handleOpenFilterDrawer = () => {
    setIsFilterDrawerOpen(true);
  };

  const { sideNav } = useGridInputs();

  return (
    <GridLayoutTrackingProvider>
      <ToastContext>
        <GridContainer {...trackingTags}>
          <ToastMessages />
          <GridSpotlights />
          <MobileGridDiscoveryGuide />
          {showBuilderSideNav && sideNav?.shouldShowCategoryNav && (
            <BuilderCategoryNavRenderer />
          )}
          <MobileGridHeader
            isFilterDrawerOpen={isFilterDrawerOpen}
            setIsFilterDrawerOpen={setIsFilterDrawerOpen}
          />
          {/* Disable new pricing UI for Checkout Interstitial pages (quickview grids) */}
          {isNewPricingUiEnabled && !isQuickViewEnabled ? (
            <VirtualScrollGrid
              virtualList={mergedAssetsAndProducts}
              totalNumResults={totalNumResults}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isPrimaryProductsFetching={isPrimaryProductsFetching}
              isPrimaryProductsLoading={isPrimaryProductsLoading}
              handleOpenFilterDrawer={handleOpenFilterDrawer}
            />
          ) : (
            <MobileGridVirtualScroll
              virtualList={mergedAssetsAndProducts}
              totalNumResults={totalNumResults}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isPrimaryProductsFetching={isPrimaryProductsFetching}
              isPrimaryProductsLoading={isPrimaryProductsLoading}
              handleOpenFilterDrawer={handleOpenFilterDrawer}
            />
          )}
        </GridContainer>
      </ToastContext>
    </GridLayoutTrackingProvider>
  );
};

export default MobileGridLayout;
